import { useMemo } from 'react'
import uniq from 'lodash/uniq'
import { PERMISSION_ACTION_EDIT } from '@klear/constants'
import type { DocumentDetail } from '@klear/document/trader-document'
import type { OrderEventContributions } from '@klear/order-event/document-contribution.wire'
import { trpc } from '@klear/ui/lib/trpc'
import type { OrderEventDocumentRef } from '@klear/upload/order-event-documents'
import { recordReviewChecks, type RecordReviewChecks } from '@/app/receivables/review-checks'

const MAX_FILED_DOCUMENTS = 200

function loaded<T>(results: readonly { data?: T; isError: boolean }[]) {
  return {
    data: results.flatMap((result) => (result.data == null ? [] : [result.data])),
    failed: results.some((result) => result.isError)
  }
}

export function useRecordReviewChecks(
  recordIds: readonly number[],
  documentsFor: (orderEventId: number) => OrderEventDocumentRef[],
  open: boolean
) {
  const utils = trpc.useUtils()
  const enabled = open && recordIds.length > 0
  const { data: filed, isError: listFailed } = trpc.documents.list.useQuery(
    { orderEventIds: [...recordIds], statuses: ['filed'], limit: MAX_FILED_DOCUMENTS },
    { enabled }
  )
  const details: { data: DocumentDetail[]; failed: boolean } = trpc.useQueries(
    (t) => (filed?.rows ?? []).map((row) => t.documents.get({ id: row.document.id }, { enabled })),
    { combine: loaded }
  )
  const contributions: { data: OrderEventContributions[]; failed: boolean } = trpc.useQueries(
    (t) => recordIds.map((orderEventId) => t.orderEvent.listDocumentContributions({ orderEventId }, { enabled })),
    { combine: loaded }
  )
  const reviewerIds = uniq(
    details.data.flatMap((detail) =>
      detail.decision?.reviewedBy != null && detail.decision.reviewedBy > 0 ? [detail.decision.reviewedBy] : []
    )
  ).sort((a, b) => a - b)
  const { data: reviewers } = trpc.user.list.useQuery(
    { ids: reviewerIds, page: 1, limit: reviewerIds.length },
    { enabled: enabled && reviewerIds.length > 0 }
  )
  const { data: timeline } = trpc.reporting.eventTimeline.useQuery(
    { eventIds: [...recordIds], actions: [PERMISSION_ACTION_EDIT] },
    { enabled }
  )

  const byRecord = useMemo(() => {
    const reviewerNames = new Map(
      (reviewers?.items ?? []).flatMap((user) =>
        user.identity.kind === 'saved' ? [[user.identity.id, user.fullName] as const] : []
      )
    )
    return new Map<number, RecordReviewChecks>(
      recordIds.map((recordId) => [
        recordId,
        recordReviewChecks({
          recordId,
          documentIds: documentsFor(recordId).map((document) => document.id),
          details: details.data,
          reviewerNames,
          contributions: contributions.data.find((one) => one.orderEventId === recordId),
          amountEdits: timeline?.events ?? []
        })
      ])
    )
  }, [recordIds, documentsFor, details.data, reviewers, contributions.data, timeline])

  const refresh = () =>
    Promise.all([
      utils.documents.list.invalidate(),
      utils.documents.get.invalidate(),
      utils.user.list.invalidate(),
      utils.orderEvent.listDocumentContributions.invalidate(),
      utils.reporting.eventTimeline.invalidate()
    ])

  return {
    checksOf: (recordId: number | null | undefined) => (recordId == null ? undefined : byRecord.get(recordId)),
    failed: listFailed || details.failed || contributions.failed,
    refresh
  }
}
